import * as React from 'react';
import styled from 'styled-components';
import { CopyOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { BlackSpan, GraySpan } from 'components/Custom/spans';
import { ReactComponent as PopOutIcon } from 'assets/images/icons/externalLinkIcon.svg';

export interface CardInfosProps {
  label: string;
  data: any;
  wrapData?: boolean;
  haveBorder?: boolean;
  horizontal?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Gap between label row and value (vertical layout only). Pass `8` or `"8px"`. */
  labelDataGap?: string | number;
  /** When true, value text may break long paths/URLs (vertical layout only). */
  dataBreakWord?: boolean;
  /** When true, constrain data wrapper so truncated content (e.g. ellipsis) works */
  truncate?: boolean;
  /** When set, the value renders as an external link (new tab). */
  dataLinkHref?: string;
  /** Plain value text with an external-link icon opening this URL (new tab). */
  externalLinkHref?: string;
  /** Accessible label / tooltip for `externalLinkHref` icon button. */
  externalLinkTitle?: string;
  /** When set, shows a copy icon over the value on hover and invokes this on click. */
  onCopy?: () => void;
  /** Accessible label / tooltip for the copy icon button. */
  copyTitle?: string;
}

const CardInfoRoot = styled.div<{
  $haveBorder: boolean;
  $wrapData: boolean;
  $truncate: boolean;
}>`
  ${(p) =>
    p.$haveBorder
      ? `
    border-left: 1px solid var(--color-primary, #41aaaf);
    padding-left: 10px;
    min-width: 160px;
    max-width: 160px;
  `
      : ''}
`;

const HorizontalRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  column-gap: 8px;
`;

const DataCell = styled.div<{ $wrapData: boolean; $truncate: boolean }>`
  ${(p) => (p.$wrapData ? `width: 134px;` : '')}
  ${(p) => (p.$truncate ? `overflow: hidden; min-width: 0;` : '')}
`;

const VerticalStack = styled.div<{ $gap: string }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* Flex gap is harder for global resets to clobber than margin on nested spans. */
  gap: ${(p) => p.$gap};
`;

const LabelText = styled(GraySpan)`
  display: block;
`;

const LABEL_GRAY = 'var(--color-text-muted, #5a6a6a)';
const ICON_ACTION_HOVER_BG = 'var(--color-layout-bg, #e9f6f7)';

const HoverCopyButton = styled.button`
  position: absolute;
  right: -10px;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  cursor: pointer;
  color: ${LABEL_GRAY};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  background-color: transparent;
  border-radius: 4px;

  &:hover {
    background-color: ${ICON_ACTION_HOVER_BG};
    color: ${LABEL_GRAY};
  }
`;

const ValueWithHoverCopy = styled.div`
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;

  &:hover ${HoverCopyButton} {
    opacity: 1;
    pointer-events: auto;
  }
`;

const ValueText = styled(BlackSpan)<{ $breakWord: boolean }>`
  display: block;
  ${(p) =>
    p.$breakWord
      ? `
    word-break: break-word;
    white-space: normal;
  `
      : ''}
`;

const CopyableValueButton = styled.button<{ $breakWord: boolean }>`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-dark, #042a2a);
  cursor: pointer;
  ${(p) =>
    p.$breakWord
      ? `
    word-break: break-word;
    white-space: normal;
  `
      : ''}
`;

const ExternalValueLink = styled.a<{ $breakWord: boolean }>`
  display: block;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  color: var(--color-primary, #41aaaf);
  text-decoration: underline;
  cursor: pointer;
  ${(p) =>
    p.$breakWord
      ? `
    word-break: break-word;
    white-space: normal;
  `
      : ''}
  &:hover {
    color: var(--color-primary-hover, #64bbbd);
  }
`;

const ValueInlineWrap = styled.span<{ $breakWord: boolean }>`
  display: inline;
  ${(p) =>
    p.$breakWord
      ? `
    word-break: break-word;
    white-space: normal;
  `
      : ''}
`;

const InlineValueText = styled(BlackSpan)<{ $breakWord: boolean }>`
  display: inline;
  ${(p) =>
    p.$breakWord
      ? `
    word-break: break-word;
    white-space: normal;
  `
      : ''}
`;

const InlineLinkButton = styled.a`
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: 4px;
  color: ${LABEL_GRAY};
  cursor: pointer;
  border-radius: 4px;
  text-decoration: none;

  svg {
    fill: currentColor;
  }

  &:hover {
    background-color: ${ICON_ACTION_HOVER_BG};
    color: ${LABEL_GRAY};
  }
`;

const TRAILING_ICON_SIZE = 18;

export const CardInfo: React.FC<CardInfosProps> = ({
  data,
  label,
  wrapData = false,
  haveBorder = false,
  horizontal = false,
  className,
  style,
  labelDataGap = 0,
  dataBreakWord = false,
  truncate = false,
  dataLinkHref,
  externalLinkHref,
  externalLinkTitle,
  onCopy,
  copyTitle,
}) => {
  const { t } = useTranslation();
  const resolvedExternalLinkTitle =
    externalLinkTitle ?? t('common.open-link-in-browser');
  const resolvedCopyTitle = copyTitle ?? t('common.copy');
  const labelGapCss =
    typeof labelDataGap === 'number' ? `${labelDataGap}px` : labelDataGap;

  const valueNode =
    onCopy != null ? (
      <ValueWithHoverCopy>
        <Tooltip title={resolvedCopyTitle}>
          <CopyableValueButton
            type="button"
            onClick={onCopy}
            aria-label={resolvedCopyTitle}
            $breakWord={dataBreakWord}
          >
            {data}
          </CopyableValueButton>
        </Tooltip>
        <Tooltip title={resolvedCopyTitle}>
          <HoverCopyButton
            type="button"
            onClick={onCopy}
            aria-label={resolvedCopyTitle}
          >
            <CopyOutlined style={{ fontSize: TRAILING_ICON_SIZE }} />
          </HoverCopyButton>
        </Tooltip>
      </ValueWithHoverCopy>
    ) : dataLinkHref != null && dataLinkHref !== '' ? (
      <ExternalValueLink
        href={dataLinkHref}
        target="_blank"
        rel="noopener noreferrer"
        $breakWord={dataBreakWord}
      >
        {data}
      </ExternalValueLink>
    ) : externalLinkHref != null && externalLinkHref !== '' ? (
      <ValueInlineWrap $breakWord={dataBreakWord}>
        <InlineValueText $breakWord={dataBreakWord}>{data}</InlineValueText>
        <Tooltip title={resolvedExternalLinkTitle}>
          <InlineLinkButton
            href={externalLinkHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={resolvedExternalLinkTitle}
          >
            <PopOutIcon
              aria-hidden
              style={{
                width: TRAILING_ICON_SIZE,
                height: TRAILING_ICON_SIZE,
                display: 'block',
              }}
            />
          </InlineLinkButton>
        </Tooltip>
      </ValueInlineWrap>
    ) : (
      <ValueText $breakWord={dataBreakWord}>{data}</ValueText>
    );

  if (horizontal) {
    return (
      <CardInfoRoot
        className={className}
        style={style}
        $haveBorder={haveBorder}
        $wrapData={wrapData}
        $truncate={truncate}
      >
        <HorizontalRow>
          <GraySpan>{label}</GraySpan>
          <DataCell $wrapData={wrapData} $truncate={truncate}>
            {valueNode}
          </DataCell>
        </HorizontalRow>
      </CardInfoRoot>
    );
  }

  return (
      <CardInfoRoot
        className={className}
        style={style}
        $haveBorder={haveBorder}
        $wrapData={wrapData}
        $truncate={truncate}
      >
      <VerticalStack $gap={labelGapCss}>
        <LabelText>{label}</LabelText>
        <DataCell $wrapData={wrapData} $truncate={truncate}>
          {valueNode}
        </DataCell>
      </VerticalStack>
    </CardInfoRoot>
  );
};
